import React, { useState } from 'react';
import { Eye, EyeOff, KeyRound, Loader2, Mail, Save, UserCog, UserRound } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import API from '../../utils/api';
import { useAdmin } from '../../context/AdminContext';
import './MyAccount.css';

const PasswordInput = ({ label, value, onChange, autoComplete, placeholder }) => {
    const [show, setShow] = useState(false);
    return (
        <label className="my-account-field">
            <span>{label}</span>
            <div className="my-account-input">
                <KeyRound size={17} aria-hidden="true" />
                <input type={show ? 'text' : 'password'} value={value} onChange={onChange} autoComplete={autoComplete} placeholder={placeholder} />
                <button type="button" onClick={() => setShow(s => !s)} aria-label={show ? 'Hide password' : 'Show password'}>
                    {show ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
            </div>
        </label>
    );
};

const MyAccount = () => {
    const { admin, setAdmin } = useAdmin();
    const [name, setName] = useState(admin?.name || '');
    const [email, setEmail] = useState(admin?.email || '');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);

    const emailChanged = email.trim().toLowerCase() !== (admin?.email || '').toLowerCase();
    const needsCurrent = emailChanged || !!newPassword;

    const handleSubmit = async event => {
        event.preventDefault();
        if (newPassword && newPassword !== confirmPassword) {
            toast.error('New password and confirmation do not match');
            return;
        }
        if (needsCurrent && !currentPassword) {
            toast.error('Enter your current password to change the email or password');
            return;
        }
        setSaving(true);
        try {
            const res = await API.put('/auth/admin-account', {
                name, email,
                ...(needsCurrent ? { currentPassword } : {}),
                ...(newPassword ? { newPassword } : {}),
            });
            setAdmin(current => ({ ...current, ...res.data.user }));
            setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
            toast.success(res.data.message || 'Account updated');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not update account');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="my-account-layout">
            <Sidebar />
            <main className="admin-content my-account-content">
                <Navbar />
                <div className="my-account-page">
                    <header className="my-account-heading">
                        <div className="my-account-eyebrow"><UserCog size={14} /> Settings</div>
                        <h1>My account</h1>
                        <p>Update your admin name, sign-in email and password.</p>
                    </header>

                    <form className="my-account-card" onSubmit={handleSubmit}>
                        <h2>Profile</h2>
                        <label className="my-account-field">
                            <span>Name</span>
                            <div className="my-account-input">
                                <UserRound size={17} aria-hidden="true" />
                                <input type="text" value={name} onChange={e => setName(e.target.value)} autoComplete="name" required />
                            </div>
                        </label>
                        <label className="my-account-field">
                            <span>Email (used to sign in)</span>
                            <div className="my-account-input">
                                <Mail size={17} aria-hidden="true" />
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" required />
                            </div>
                        </label>

                        <h2>Password</h2>
                        <p className="my-account-hint">Leave the new password empty to keep your current one. At least 6 characters with an uppercase letter, a number and a symbol.</p>
                        <PasswordInput label="Current password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} autoComplete="current-password"
                            placeholder={needsCurrent ? 'Required to save these changes' : 'Only needed to change email or password'} />
                        <PasswordInput label="New password" value={newPassword} onChange={e => setNewPassword(e.target.value)} autoComplete="new-password" placeholder="New password" />
                        <PasswordInput label="Confirm new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} autoComplete="new-password" placeholder="Repeat new password" />

                        <button type="submit" className="my-account-save" disabled={saving}>
                            {saving ? <Loader2 size={17} className="my-account-spin" /> : <Save size={17} />} Save changes
                        </button>
                    </form>
                </div>
            </main>
        </div>
    );
};

export default MyAccount;
